import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CartItem, CartLine, MarketplaceProduct } from '@/types';
import { cartService } from '@/services/marketplace/cartService';
import { marketplaceService } from '@/services/marketplace/marketplaceService';

interface CartContextType {
  items: CartItem[];
  lines: CartLine[];
  count: number;
  add: (productId: string, quantity?: number, variantIndex?: number | null) => void;
  remove: (productId: string, variantIndex?: number | null) => void;
  setQuantity: (productId: string, quantity: number, variantIndex?: number | null) => void;
  clear: () => void;
  /** ms since the cart was last modified (cart-abandonment agent), or null. */
  lastUpdatedAt: number | null;
}

const Ctx = createContext<CartContextType | undefined>(undefined);

/** Two lines are "the same" cart line only if both productId AND the selected variant match. */
const sameLine = (i: CartItem, productId: string, variantIndex: number | null) => i.productId === productId && (i.variantIndex ?? null) === variantIndex;

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => cartService.load());
  const [products, setProducts] = useState<Record<string, MarketplaceProduct>>({});

  // Load the catalog once so cart lines can be rendered with full product data.
  useEffect(() => {
    void (async () => {
      const all = await marketplaceService.listAll();
      setProducts(Object.fromEntries(all.map((p) => [p.id, p])));
    })();
  }, []);

  const add = useCallback(
    (productId: string, quantity = 1, variantIndex: number | null = null) => {
      setItems((prev) => {
        const existing = prev.find((i) => sameLine(i, productId, variantIndex));
        const next = existing
          ? prev.map((i) => (sameLine(i, productId, variantIndex) ? { ...i, quantity: i.quantity + quantity } : i))
          : [...prev, { productId, variantIndex, quantity, addedAt: new Date().toISOString() }];
        cartService.save(next);
        return next;
      });
    },
    [],
  );

  const remove = useCallback((productId: string, variantIndex: number | null = null) => {
    setItems((prev) => {
      const next = prev.filter((i) => !sameLine(i, productId, variantIndex));
      cartService.save(next);
      return next;
    });
  }, []);

  const setQuantity = useCallback(
    (productId: string, quantity: number, variantIndex: number | null = null) => {
      if (quantity <= 0) return remove(productId, variantIndex);
      setItems((prev) => {
        const next = prev.map((i) => (sameLine(i, productId, variantIndex) ? { ...i, quantity } : i));
        cartService.save(next);
        return next;
      });
    },
    [remove],
  );

  const clear = useCallback(() => {
    cartService.clear();
    setItems([]);
  }, []);

  const lines = useMemo<CartLine[]>(
    () =>
      items
        .map((i) => {
          const product = products[i.productId];
          if (!product) return null;
          const variantIndex = i.variantIndex ?? null;
          const variant = variantIndex != null ? (product.variants?.[variantIndex] ?? null) : null;
          return { product, variantIndex, variant, quantity: i.quantity };
        })
        .filter((l): l is CartLine => l !== null),
    [items, products],
  );

  const count = useMemo(() => items.reduce((n, i) => n + i.quantity, 0), [items]);

  return (
    <Ctx.Provider value={{ items, lines, count, add, remove, setQuantity, clear, lastUpdatedAt: cartService.lastUpdatedAt() }}>
      {children}
    </Ctx.Provider>
  );
}

export function useCartContext(): CartContextType {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCartContext must be used within a CartProvider');
  return ctx;
}
